import type { ProductFormValues } from './schemas';
import type { Product } from './types';

/**
 * The request bodies for `POST` / `PUT /api/v1/merchant/products`.
 *
 * =====================================================================
 * WHY THE UPDATE BODY IS BUILT FROM THE CURRENT PRODUCT
 * =====================================================================
 *
 * `PUT /merchant/products/:id` is a FULL REPLACE by contract (asserted by
 * the API's own e2e suite): a field left out of the body, or sent as
 * `null`, is stored as NULL. That is a fine contract for a client that
 * owns every field — and this client does not: `ProductForm` renders no
 * input for `barcode`, `purchasePrice`, `vatRate`, `weight`, `dimensions`
 * or `metadata`. Sending only what the form knows therefore ERASED those
 * six values on every save, however trivial the edit (a renamed product
 * lost its purchase price, VAT rate, weight, barcode, dimensions and
 * metadata, with a success toast and no error anywhere).
 *
 * The rule now is: for those six fields, `undefined` (the form has no
 * opinion) means "leave as it is" and is filled from the product as it is
 * stored right now; an explicit `null` still means "clear it", so a form
 * that one day renders one of them keeps full control of it. Every field
 * the form DOES render is taken from the form, unchanged from before.
 *
 * The API contract is deliberately NOT changed to work around this (a
 * "partial update" PUT would alter behaviour for every other client);
 * the client that does not own a field simply sends it back unchanged.
 */

/** The fields `ProductForm` does not render and must therefore send back as stored. */
export const PRESERVED_PRODUCT_FIELDS = [
  'barcode',
  'purchasePrice',
  'vatRate',
  'weight',
  'dimensions',
  'metadata',
] as const;

export type PreservedProductField = (typeof PRESERVED_PRODUCT_FIELDS)[number];

/** Everything the form renders, mapped exactly as it always was (an unset optional becomes `null`). */
function formOwnedFields(values: ProductFormValues): Record<string, unknown> {
  return {
    categoryId: values.categoryId ?? null,
    name: values.name,
    slug: values.slug,
    shortDescription: values.shortDescription ?? null,
    longDescription: values.longDescription ?? null,
    sku: values.sku,
    price: values.price,
    compareAtPrice: values.compareAtPrice ?? null,
    brand: values.brand ?? null,
    status: values.status,
    seoTitle: values.seoTitle ?? null,
    seoDescription: values.seoDescription ?? null,
  };
}

/** `POST` — a NEW product has nothing to preserve; the four numeric/text fields keep the shape they always had. */
export function buildProductCreateBody(values: ProductFormValues): Record<string, unknown> {
  return {
    ...formOwnedFields(values),
    barcode: values.barcode ?? null,
    purchasePrice: values.purchasePrice ?? null,
    vatRate: values.vatRate ?? null,
    weight: values.weight ?? null,
  };
}

/**
 * `PUT` — the form's values laid over the product as it is stored NOW
 * (`current`, read immediately before the write). See the header for the
 * `undefined` / `null` rule on the six preserved fields.
 */
export function buildProductUpdateBody(
  current: Product,
  values: ProductFormValues,
): Record<string, unknown> {
  return {
    ...formOwnedFields(values),
    barcode: values.barcode !== undefined ? values.barcode : current.barcode,
    purchasePrice:
      values.purchasePrice !== undefined ? values.purchasePrice : current.purchasePrice,
    vatRate: values.vatRate !== undefined ? values.vatRate : current.vatRate,
    weight: values.weight !== undefined ? values.weight : current.weight,
    dimensions: current.dimensions,
    metadata: current.metadata,
  };
}
